/**
 * FactorLifecycleContent — renders `factor_lifecycle` timeline entries.
 *
 * Replaces four separate components (FactorDeactivated/Reactivated/Paused/Resumed). The ledger
 * has ONE `factor_lifecycle` entry type carrying `content.transition`, so the split those four
 * encoded no longer exists in the data — keeping them would have meant four components that can
 * never be selected by `eventType` alone.
 *
 * `resumed` has no writer today (only deactivated / reactivated / paused are emitted), but it is
 * kept in the map: the value is part of the vocabulary, and rendering a raw key if one ever lands
 * is worse than an unused branch.
 */

import React from 'react';
import { useTranslation } from 'localization';
import { TimelineEvent } from 'types/timeline';

interface FactorLifecycleContentProps {
  event: TimelineEvent;
}

/** transition -> i18n key. Unknown transitions fall back to a generic line rather than a raw key. */
const TRANSITION_KEYS: Record<string, string> = {
  deactivated: 'timeline.eventContent.deactivatedFactor',
  reactivated: 'timeline.eventContent.reactivatedFactor',
  paused: 'timeline.eventContent.pausedFactor',
  resumed: 'timeline.eventContent.resumedFactor',
};

const FactorLifecycleContent: React.FC<FactorLifecycleContentProps> = ({ event }) => {
  const { t } = useTranslation();
  const payload = (event.payload ?? {}) as { transition?: string; factorType?: string; factorId?: string };

  const transition = payload.transition ?? '';
  const key = TRANSITION_KEYS[transition] ?? 'timeline.eventContent.factorLifecycleChanged';

  return (
    <div className="timeline-event-content">
      <span className="timeline-event-content__text">
        {t(key, { factorType: payload.factorType ?? '', transition })}
      </span>
    </div>
  );
};

export default FactorLifecycleContent;
